import { hasOptions, type Comparable, type FieldTypeCard } from '../field-types/card';
import { isReference, type Comparison, type ComparisonValue, type Condition, type Option } from '../format/pieces';
import { holdsWhenEmpty, isOptionSet, operatorHolds } from './operators';
import { isFilled } from '../validations';

interface FieldReading {
  id: string;
  card: FieldTypeCard;
  value: unknown;
  options: Option[];
}

export type ReadField = (fieldId: string) => FieldReading | undefined;

export const conditionHolds = (condition: Condition, read: ReadField, today: string): boolean => {
  switch (condition.kind) {
    case 'and':
      return condition.conditions.every(item => conditionHolds(item, read, today));
    case 'or':
      return condition.conditions.some(item => conditionHolds(item, read, today));
    default:
      return comparisonHolds(condition, read, today);
  }
};

const comparisonHolds = (comparison: Comparison, read: ReadField, today: string): boolean => {
  const field = read(comparison.fieldId);
  if (!('value' in comparison)) {
    const filled = field !== undefined && isFilled(field.card, field.value);
    return filled === (comparison.operator === 'isFilled');
  }

  const whenEmpty = holdsWhenEmpty(comparison.operator);
  const { value } = comparison;
  const other = isReference(value) && value.kind === 'fieldValue' ? read(value.fieldId) : undefined;
  const byOptionText = field !== undefined && other !== undefined && matchesByOptionText(field, other);
  const left = byOptionText ? optionTextsOf(field) : comparableOf(field);
  if (!field || left === undefined) return whenEmpty;
  const right = byOptionText ? optionTextsOf(other) : comparedWith(value, field.card, read, today);
  if (right === undefined) return whenEmpty;
  return operatorHolds(comparison.operator, left, right) ?? whenEmpty;
};

const comparableOf = (field: FieldReading | undefined): Comparable | undefined => {
  if (!field || !isFilled(field.card, field.value)) return undefined;
  return orNothingWhenBlank(field.card.comparable?.(field.value));
};

const matchesByOptionText = (
  field: { id: string; card: FieldTypeCard },
  other: { id: string; card: FieldTypeCard },
): boolean => {
  return field.id !== other.id && hasOptions(field.card.valueKind) && hasOptions(other.card.valueKind);
};

const optionTextsOf = (field: FieldReading | undefined): Comparable | undefined => {
  const selected = comparableOf(field);
  if (field === undefined || !isOptionSet(selected)) return undefined;
  const texts = field.options
    .filter(option => selected.includes(option.id))
    .map(option => option.label.trim().toLowerCase());
  return orNothingWhenBlank(texts);
};

const comparedWith = (
  value: ComparisonValue,
  card: FieldTypeCard,
  read: ReadField,
  today: string,
): Comparable | undefined => {
  if (isReference(value)) {
    if (value.kind === 'fieldValue') return comparableOf(read(value.fieldId));
    return orNothingWhenBlank(card.comparable?.(today));
  }
  if (Array.isArray(value)) return orNothingWhenBlank(value);
  const typed =
    typeof value === 'string' && card.comparableTyped ? card.comparableTyped(value) : card.comparable?.(value);
  return orNothingWhenBlank(typed);
};

const orNothingWhenBlank = (comparable: Comparable | undefined): Comparable | undefined => {
  if (comparable === '' || (Array.isArray(comparable) && comparable.length === 0)) return undefined;
  if (typeof comparable === 'number' && !Number.isFinite(comparable)) return undefined;
  return comparable;
};
